import React, { useMemo } from 'react'
import { ModalFormId } from 'src/components/ModalForms'
import { Markup } from 'src/components/Reusable/Text/Markup'
import {
  TestTableDescription,
  TestTableDescriptionSection,
  TestTableDescriptionSectionHeader,
} from 'src/components/ModalForms/Evidence/evidence.styles'
import { getTestTableResult } from 'src/components/ModalForms/Evidence/ViewTestTableSourceModal'
import {
  EnforcementMode,
  HighlightCondition,
  TestTable,
  ThresholdOperator,
} from '@trustero/trustero-api-web/lib/auditbotapi/auditbotapi_pb'
import { FlexAlign, FlexRow } from 'src/components/Reusable/Flex'
import { LearnMoreLink } from 'src/components/Reusable/Text/Link'
import { HelpPagesInternal } from 'src/pages/Help/Help.constants'
import { cleanTestProcedureDescription } from 'src/pages/AuditBot/accordion/subsection/ControlChecks/AuditBotControlChecks.helpers'
import { SOURCE_RESULT } from '@trustero/trustero-api-web/lib/source/source_pb'
import { GridRowClassNameParams } from '@mui/x-data-grid'
import { ControlCheckSourceTitle } from '../artifacts/AuditScan/AuditScanArtifact.components'
import { TabularArtifactModal } from './TabularArtifactModal'

export const TestTableArtifactModal = ({
  testTable,
}: {
  testTable: TestTable
}): JSX.Element => {
  const explanation = testTable?.getExplanation() || ''
  const headerTooltip = useMemo(
    () => ({
      header: 'Result',
      tooltip: explanation,
    }),
    [explanation],
  )
  const tableCsv = testTable?.getTable() || ''

  if (!testTable) {
    return <></>
  }

  const passed = testTable.getPassed()
  const result = passed === true ? SOURCE_RESULT.PASS : SOURCE_RESULT.FAIL
  const sourceId = testTable.getSourceId()
  const sourceType = testTable.getSourceType()
  const count = testTable.getCount()
  const threshold = testTable.getThresholds()?.getThreshold() || 0
  const mode = testTable.getThresholds()?.getMode() || EnforcementMode.ABSOLUTE
  const operator =
    testTable.getThresholds()?.getOperator() ||
    ThresholdOperator.GREATER_THAN_OR_EQUAL
  const highlightCondition = testTable.getThresholds()?.getHighlight()

  const description: JSX.Element = (
    <TestTableDescription>
      <FlexRow justify={FlexAlign.FLEX_START} isFullWidth>
        This table shows how evidence from a table performed on a specific test
        procedure. &nbsp;
        <LearnMoreLink href={HelpPagesInternal['TestTable'].link} />
      </FlexRow>
      <TestTableDescriptionSection>
        {getTestTableResult(passed, count, threshold, mode, operator)}
      </TestTableDescriptionSection>
      <TestTableDescriptionSection>
        <TestTableDescriptionSectionHeader>
          Procedure
        </TestTableDescriptionSectionHeader>
        <Markup markdown={cleanTestProcedureDescription(testTable.getTest())} />
      </TestTableDescriptionSection>
      <TestTableDescriptionSection>
        <TestTableDescriptionSectionHeader>
          Source
        </TestTableDescriptionSectionHeader>
        <ControlCheckSourceTitle
          sourceId={sourceId}
          sourceType={sourceType}
          result={result}
        />
      </TestTableDescriptionSection>
    </TestTableDescription>
  )

  const title = testTable.getCaption()

  return (
    <TabularArtifactModal
      formId={ModalFormId.TEST_TABLE_ARTIFACT_MODAL}
      title={title}
      description={description}
      csv={tableCsv}
      headerTooltip={headerTooltip}
      getRowClassName={(params: GridRowClassNameParams) => {
        const failed =
          (highlightCondition === HighlightCondition.WHEN_FALSE &&
            params.row['Result'] === 'False') ||
          (highlightCondition === HighlightCondition.WHEN_TRUE &&
            params.row['Result'] === 'True')
        return failed ? 'failed' : ''
      }}
    />
  )
}
